import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  QrCode,
  Smartphone,
  Banknote,
  ArrowLeft,
  Copy,
  Check,
  Download,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  User,
  FileText,
  DollarSign,
  RefreshCw,
  Phone,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Checkout = () => {
  const { isDarkMode } = useOutletContext() || {};
  const navigate = useNavigate();
  
  // Form state
  const [step, setStep] = useState('form'); // form, payment, complete
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'LRD',
    description: '',
    customer_name: '',
    notes: ''
  });
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('MTN');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  
  // Transaction state
  const [transaction, setTransaction] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Poll for transaction status updates (for QR and Mobile Money)
  useEffect(() => {
    let interval;
    if (transaction && 
        (transaction.payment_method === 'qr' || transaction.payment_method === 'mobile_money') &&
        transaction.status === 'pending') {
      interval = setInterval(async () => {
        setCheckingStatus(true);
        try {
          const res = await fetch(`${API_URL}/api/merchant/transactions/${transaction.transaction_id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'confirmed') {
              setTransaction(prev => ({ ...prev, status: 'confirmed' }));
              setStep('complete');
              toast.success('Payment confirmed!');
              clearInterval(interval);
            }
          }
        } catch (error) {
          console.error('Error checking status:', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 5000); // Check every 5 seconds
    }
    return () => clearInterval(interval);
  }, [transaction]);

  const handleCreateCheckout = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description) {
      toast.error('Please enter a description');
      return;
    }
    if (!selectedMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description,
        customer_name: formData.customer_name || null,
        notes: formData.notes || null,
        payment_method: selectedMethod
      };

      if (selectedMethod === 'mobile_money') {
        payload.network = selectedNetwork;
      }

      const res = await fetch(`${API_URL}/api/merchant/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setTransaction(data);
        
        if (selectedMethod === 'cash') {
          setStep('payment');
        } else {
          setStep('payment');
        }
        
        toast.success('Checkout created successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create checkout');
      }
    } catch (error) {
      toast.error('Failed to create checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCash = async () => {
    if (!transaction) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/merchant/checkout/${transaction.transaction_id}/confirm-cash`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        setTransaction(prev => ({ ...prev, status: 'confirmed' }));
        setStep('complete');
        toast.success('Cash payment confirmed!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to confirm payment');
      }
    } catch (error) {
      toast.error('Failed to confirm payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCheckout = async () => {
    if (!transaction) return;
    
    try {
      const res = await fetch(`${API_URL}/api/merchant/checkout/${transaction.transaction_id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Checkout cancelled');
        resetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const resetForm = () => {
    setStep('form');
    setFormData({ amount: '', currency: 'LRD', description: '', customer_name: '', notes: '' });
    setSelectedMethod(null);
    setTransaction(null);
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied!');
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadQRCode = () => {
    if (!transaction?.qr_code) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${transaction.qr_code}`;
    link.download = `Payment-${transaction.reference}.png`;
    link.click();
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'qr': return QrCode;
      case 'mobile_money': return Smartphone;
      case 'cash': return Banknote;
      default: return DollarSign;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700"><Clock className="w-4 h-4" /> Waiting for Payment</span>;
      case 'awaiting_confirmation':
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700"><Clock className="w-4 h-4" /> Awaiting Confirmation</span>;
      case 'confirmed':
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-4 h-4" /> Confirmed</span>;
      case 'cancelled':
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700"><XCircle className="w-4 h-4" /> Cancelled</span>;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="checkout-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {step !== 'form' && (
            <button
              onClick={resetForm}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              {step === 'form' ? 'New Checkout' : step === 'payment' ? 'Complete Payment' : 'Payment Complete'}
            </h1>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {step === 'form' ? 'Create a new sale and select payment method' : 
               step === 'payment' ? 'Follow the instructions to complete the payment' : 
               'Transaction has been confirmed'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Main Content */}
        <div className={`flex-1 rounded-xl p-6 overflow-y-auto ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          
          {/* Step 1: Form */}
          {step === 'form' && (
            <div className="max-w-2xl mx-auto">
              {/* Amount Input */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Amount *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <DollarSign className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className={`w-full pl-10 pr-4 py-3 text-2xl rounded-lg border ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-white border-gray-300 text-slate-800'
                      }`}
                      data-testid="amount-input"
                    />
                  </div>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className={`w-28 px-3 py-3 text-lg rounded-lg border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  >
                    <option value="LRD">LRD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description *
                </label>
                <div className="relative">
                  <FileText className={`absolute left-3 top-3 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Product purchase, Service fee"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                        : 'bg-white border-gray-300 text-slate-800'
                    }`}
                    data-testid="description-input"
                  />
                </div>
              </div>

              {/* Customer Name (Optional) */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Customer Name (Optional)
                </label>
                <div className="relative">
                  <User className={`absolute left-3 top-3 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Customer name"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                        : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  />
                </div>
              </div>

              {/* Notes (Optional) */}
              <div className="mb-8">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={2}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDarkMode
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                      : 'bg-white border-gray-300 text-slate-800'
                  }`}
                />
              </div>

              {/* Payment Method Selection */}
              <div className="mb-8">
                <label className={`block text-sm font-medium mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Select Payment Method
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {/* QR Payment */}
                  <button
                    type="button"
                    onClick={() => setSelectedMethod('qr')}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      selectedMethod === 'qr'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                        : isDarkMode
                          ? 'border-slate-600 hover:border-slate-500 bg-slate-700'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="method-qr"
                  >
                    <div className={`w-16 h-16 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                      selectedMethod === 'qr' ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-600'
                    }`}>
                      <QrCode className="w-8 h-8" />
                    </div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>QR Code</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Customer scans & pays
                    </p>
                  </button>

                  {/* Mobile Money */}
                  <button
                    type="button"
                    onClick={() => setSelectedMethod('mobile_money')}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      selectedMethod === 'mobile_money'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                        : isDarkMode
                          ? 'border-slate-600 hover:border-slate-500 bg-slate-700'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="method-mobile"
                  >
                    <div className={`w-16 h-16 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                      selectedMethod === 'mobile_money' ? 'bg-teal-500 text-white' : 'bg-orange-100 text-orange-600'
                    }`}>
                      <Smartphone className="w-8 h-8" />
                    </div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Mobile Money</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      MTN or Orange
                    </p>
                  </button>

                  {/* Cash */}
                  <button
                    type="button"
                    onClick={() => setSelectedMethod('cash')}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      selectedMethod === 'cash'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                        : isDarkMode
                          ? 'border-slate-600 hover:border-slate-500 bg-slate-700'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="method-cash"
                  >
                    <div className={`w-16 h-16 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                      selectedMethod === 'cash' ? 'bg-teal-500 text-white' : 'bg-green-100 text-green-600'
                    }`}>
                      <Banknote className="w-8 h-8" />
                    </div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Cash</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Manual confirmation
                    </p>
                  </button>
                </div>
              </div>

              {/* Network Selection (for Mobile Money) */}
              {selectedMethod === 'mobile_money' && (
                <div className="mb-8">
                  <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Select Network
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setSelectedNetwork('MTN')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        selectedNetwork === 'MTN'
                          ? 'border-yellow-500 bg-yellow-50'
                          : isDarkMode
                            ? 'border-slate-600 bg-slate-700'
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      <p className={`font-semibold ${selectedNetwork === 'MTN' ? 'text-yellow-700' : (isDarkMode ? 'text-white' : 'text-slate-800')}`}>
                        MTN
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedNetwork('Orange')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        selectedNetwork === 'Orange'
                          ? 'border-orange-500 bg-orange-50'
                          : isDarkMode
                            ? 'border-slate-600 bg-slate-700'
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      <p className={`font-semibold ${selectedNetwork === 'Orange' ? 'text-orange-700' : (isDarkMode ? 'text-white' : 'text-slate-800')}`}>
                        Orange
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {/* Create Button */}
              <button
                onClick={handleCreateCheckout}
                disabled={loading || !selectedMethod || !formData.amount || !formData.description}
                className="w-full py-4 bg-teal-500 text-white text-lg font-semibold rounded-xl hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                data-testid="create-checkout-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Checkout
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 'payment' && transaction && (
            <div className="max-w-xl mx-auto text-center">
              {/* Transaction Summary */}
              <div className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Amount</p>
                <p className={`text-4xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {transaction.currency} {parseFloat(transaction.amount).toLocaleString()}
                </p>
                {formData.customer_name && (
                  <p className={`mt-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Customer: {formData.customer_name}
                  </p>
                )}
                <div className="mt-4">
                  {getStatusBadge(transaction.status)}
                </div>
              </div>

              {/* Reference */}
              <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Reference Number</p>
                <div className="flex items-center justify-center gap-2">
                  <span className={`text-xl font-mono font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {transaction.reference}
                  </span>
                  <button
                    onClick={() => copyToClipboard(transaction.reference, 'ref')}
                    className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600"
                  >
                    {copied === 'ref' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              {/* QR Payment Display */}
              {transaction.payment_method === 'qr' && transaction.qr_code && (
                <div>
                  <div className={`inline-block p-4 rounded-2xl ${isDarkMode ? 'bg-white' : 'bg-gray-50'}`}>
                    <img
                      src={`data:image/png;base64,${transaction.qr_code}`}
                      alt="QR Code"
                      className="w-56 h-56 mx-auto"
                    />
                  </div>
                  
                  <div className="flex gap-3 justify-center mt-4">
                    <button
                      onClick={downloadQRCode}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                        isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-slate-700'
                      }`}
                    >
                      <Download className="w-4 h-4" />
                      Download QR
                    </button>
                  </div>

                  <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {checkingStatus && <RefreshCw className="w-4 h-4 inline animate-spin mr-2" />}
                      Customer should scan the QR code and pay. Transaction will auto-confirm when payment is received.
                    </p>
                  </div>
                </div>
              )}

              {/* Mobile Money Display */}
              {transaction.payment_method === 'mobile_money' && (
                <div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'} mb-4`}>
                    <AlertCircle className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                    <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      Customer should include reference <strong>{transaction.reference}</strong> in the payment message
                    </p>
                  </div>

                  {transaction.payment_sources && transaction.payment_sources.length > 0 && (
                    <div className="space-y-3">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Send payment to:
                      </p>
                      {transaction.payment_sources.map((source, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            source.network === 'MTN'
                              ? 'bg-yellow-50 border-yellow-200'
                              : 'bg-orange-50 border-orange-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Phone className={`w-5 h-5 ${source.network === 'MTN' ? 'text-yellow-600' : 'text-orange-600'}`} />
                              <div className="text-left">
                                <p className={`font-semibold ${source.network === 'MTN' ? 'text-yellow-700' : 'text-orange-700'}`}>
                                  {source.network}
                                </p>
                                <p className="font-mono text-lg">{source.phone_number}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => copyToClipboard(source.phone_number, `phone-${idx}`)}
                              className="p-2 rounded-lg hover:bg-white/50"
                            >
                              {copied === `phone-${idx}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {checkingStatus && <RefreshCw className="w-4 h-4 inline animate-spin mr-2" />}
                      Transaction will auto-confirm when SMS payment notification is received.
                    </p>
                  </div>
                </div>
              )}

              {/* Cash Payment Display */}
              {transaction.payment_method === 'cash' && (
                <div>
                  <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                    <Banknote className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                      Collect Cash Payment
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      Collect {transaction.currency} {parseFloat(transaction.amount).toLocaleString()} from the customer
                    </p>
                  </div>

                  <button
                    onClick={handleConfirmCash}
                    disabled={loading}
                    className="w-full mt-6 py-4 bg-green-500 text-white text-lg font-semibold rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-cash-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        Mark as Cash Paid
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Cancel Button */}
              <button
                onClick={handleCancelCheckout}
                className={`mt-4 px-6 py-2 rounded-lg ${
                  isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'
                }`}
              >
                Cancel Transaction
              </button>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 'complete' && transaction && (
            <div className="max-w-md mx-auto text-center py-8">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Payment Confirmed!
              </h2>
              <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Transaction has been successfully completed
              </p>

              <div className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className={`text-3xl font-bold text-green-500 mb-2`}>
                  {transaction.currency} {parseFloat(transaction.amount).toLocaleString()}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Reference: {transaction.reference}
                </p>
                {formData.customer_name && (
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Customer: {formData.customer_name}
                  </p>
                )}
                <div className="flex items-center justify-center gap-2 mt-3">
                  {React.createElement(getMethodIcon(transaction.payment_method), { className: 'w-4 h-4' })}
                  <span className={`text-sm capitalize ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {transaction.payment_method.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={resetForm}
                  className="flex-1 py-3 bg-teal-500 text-white font-semibold rounded-xl hover:bg-teal-600"
                >
                  New Checkout
                </button>
                <button
                  onClick={() => navigate('/app/transactions')}
                  className={`flex-1 py-3 rounded-xl font-semibold ${
                    isDarkMode
                      ? 'bg-slate-700 text-white hover:bg-slate-600'
                      : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                  }`}
                >
                  View Transactions
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
